import React, { useEffect } from 'react';
import {
  ChoiceGroup,
  Dropdown,
  getTheme,
  IChoiceGroupOption,
  IDropdownOption,
  Stack,
  Text,
  TextField
} from '@fluentui/react';
import { QueueConfiguration, Title } from '../../services';
import { settingsDropdownRoleStyles, settingsFieldStyles } from '../admin/SettingsStyles';

export interface FlowFormProps {
  onFormComplete: React.Dispatch<React.SetStateAction<boolean>>;
  onFormChange: (updatedQueue: QueueConfiguration) => void;
  currentQueue: QueueConfiguration;
}

interface DetailFormProps extends FlowFormProps {
  validTitles: Title[];
}

export const DetailsForm: React.FC<DetailFormProps> = props => {
  const { palette, fonts } = getTheme();
  const [showErrorDisplayName, setErrorDisplayName] = React.useState<boolean>(false);
  const [showErrorSubjectType, setErrorSubjectType] = React.useState<boolean>(false);
  const minLength = 3;
  const maxLength = 32;

  const formIsValid = (): boolean => {
    return (
      !!props.currentQueue?.displayName &&
      !showErrorDisplayName &&
      !!props.currentQueue?.title?.displayName &&
      !!props.currentQueue?.title?.titleId &&
      !!props.currentQueue?.contentType &&
      !!props.currentQueue?.subjectType &&
      !showErrorSubjectType
    );
  };

  useEffect(() => {
    if (formIsValid()) {
      props.onFormComplete(true);
    } else {
      props.onFormComplete(false);
    }
  }, [props, showErrorSubjectType, showErrorDisplayName]);

  const onValidDisplayNameChange = (newValue?: string) => {
    if (!newValue || newValue.length > maxLength || newValue.length < minLength) {
      setErrorDisplayName(true);
    } else {
      setErrorDisplayName(false);
    }
    const updatedQueue = {
      ...props.currentQueue,
      displayName: newValue
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const onTitleDisplayNameChange = (item?: IDropdownOption) => {
    if (item) {
      const updatedQueue = {
        ...props.currentQueue,
        title: { displayName: item.text, titleId: item.key }
      } as QueueConfiguration;
      props.onFormChange(updatedQueue);
    }
  };

  const onContentTypeChange = (item?: IChoiceGroupOption) => {
    if (item) {
      const updatedQueue = {
        ...props.currentQueue,
        contentType: item.key
      } as QueueConfiguration;
      props.onFormChange(updatedQueue);
    }
  };

  const onSubjectTypeChange = (newValue?: string): void => {
    if (!newValue || newValue.length > maxLength || newValue.length < minLength || !/^[a-z0-9]+$/i.exec(newValue)) {
      setErrorSubjectType(true);
    } else {
      setErrorSubjectType(false);
    }
    const updatedQueue = {
      ...props.currentQueue,
      subjectType: newValue
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const titleDisplayOptions: IDropdownOption[] = props.validTitles.map(title => {
    return { key: title.titleId, text: title.displayName };
  });

  const contentTypeOptions: IChoiceGroupOption[] = [
    { key: 'image', text: 'Image' },
    { key: 'json', text: 'Json' },
    { key: 'text', text: 'Text' },
    { key: 'video', text: 'Video' }
  ];

  return (
    <Stack>
      <Text className="my-2" variant={'xLarge'}>
        {'Details'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`These details should be pulled from your onboarding document that was created during set up \
        with your Microsoft account manager. If you have additional questions, contact your account manager.`}
      </Text>
      <Stack style={{ marginTop: 16 }} />

      <TextField
        value={props.currentQueue.displayName}
        label="Display Name"
        onChange={(e, newValue): void => onValidDisplayNameChange(newValue)}
        errorMessage={showErrorDisplayName ? `Name must be between ${minLength} and ${maxLength} characters` : ''}
        styles={settingsFieldStyles(palette, fonts, 400)}
        placeholder={'The name of your queue'}
        required
      />
      <Stack style={{ marginTop: 16 }} />
      <Dropdown
        label={'Title display name'}
        placeholder={'Select from the dropdown'}
        options={titleDisplayOptions}
        onChange={(e, selectedItem): void => onTitleDisplayNameChange(selectedItem)}
        styles={settingsDropdownRoleStyles(palette, fonts, 400)}
        required
      />
      <Stack style={{ marginTop: 16 }} />
      <ChoiceGroup
        value={props.currentQueue.contentType}
        defaultSelectedKey="B"
        options={contentTypeOptions}
        onChange={(e, selectedItem): void => onContentTypeChange(selectedItem)}
        label="Pick one"
        required
      />
      <Stack style={{ marginTop: 16 }} />
      <TextField
        value={props.currentQueue.subjectType}
        label="Subject Type"
        onChange={(e, newValue): void => onSubjectTypeChange(newValue)}
        errorMessage={
          showErrorSubjectType ? `Name must be alphanumeric and between ${minLength} and ${maxLength} characters` : ''
        }
        styles={settingsFieldStyles(palette, fonts, 400)}
        placeholder={'eg. XboxLiveModeration'}
        required
      />
    </Stack>
  );
};
